import React, { ReactNode, useMemo } from 'react';
import { NormalButton } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';

import {
  IllustCompleted,
  IllustError,
  IllustInquiryEmpty,
  IllustNoGoods,
  IllustNoSearch,
  IllustNoWifi,
  IllustWarning,
} from '@common/icons';
import { BrandColorType, useBrandStyle } from '@common/styles';
import { BaseText, FullScreen, Guide, LayoutMiddle } from '@common/toolbox';

export enum NotifyIcon {
  CONFIRM = 'CONFIRM',
  CANCEL = 'CANCEL',
  EMPTY = 'EMPTY',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  NO_GOODS = 'NO_GOODS',
  NO_SEARCH = 'NO_SEARCH',
  NO_WIFI = 'NO_WIFI',
}

interface NotifyProps {
  icon_type?: NotifyIcon;
  title: JSX.Element | string;
  contents?: JSX.Element | string[] | string;
  onClose?: () => void;
  closeMessage?: string;
  onConfirm?: () => void;
  confirmMessage?: string;
  guideTitle?: string;
  extraBottom?: React.ReactNode;
  css?: SerializedStyles;
}

const ArrayToContents = (contents: ReactNode[]) => {
  return contents.map((content, index) => (
    <React.Fragment key={index}>
      {content}
      <br />
    </React.Fragment>
  ));
};

export const Notify = ({
  icon_type,
  title,
  contents,
  onClose,
  closeMessage,
  onConfirm,
  confirmMessage,
  guideTitle,
  extraBottom,
  ...otherProps
}: NotifyProps) => {
  const { brand_color } = useBrandStyle();

  const Icon = useMemo(() => {
    switch (icon_type) {
      case NotifyIcon.EMPTY:
        return <IllustInquiryEmpty />;
      case NotifyIcon.CANCEL:
      case NotifyIcon.CONFIRM:
        return <IllustCompleted color={brand_color.Brand_400} />;
      case NotifyIcon.ERROR:
        return <IllustError />;
      case NotifyIcon.WARNING:
        return <IllustWarning />;
      case NotifyIcon.NO_GOODS:
        return <IllustNoGoods />;
      case NotifyIcon.NO_SEARCH:
        return <IllustNoSearch />;
      case NotifyIcon.NO_WIFI:
        return <IllustNoWifi />;
    }
  }, [brand_color.Brand_400, icon_type]);

  return (
    <Container {...otherProps}>
      <LayoutMiddle>
        {!!icon_type && Icon}
        <Title as='h2' variation='BODY_18_BOLD' brand_color={brand_color}>
          {title}
        </Title>
        {!!contents && (
          <Content as='div' variation='BODY_15_REGULAR'>
            {Array.isArray(contents) ? ArrayToContents(contents) : contents}
          </Content>
        )}
        <div>
          {!!onClose && (
            <ConfirmButton
              onClick={onClose}
              style={{ background: 'var(--zds-colors-global-gray-30)', color: 'var(--zds-colors-global-gray-900)' }}
            >
              {closeMessage || '확인'}
            </ConfirmButton>
          )}

          {!!onConfirm && <ConfirmButton onClick={onConfirm}>{confirmMessage || '확인'}</ConfirmButton>}
        </div>
        {!!guideTitle && <Guide title={guideTitle}></Guide>}
        {extraBottom}
        <div
          css={css`
            height: 48px;
          `}
        />
      </LayoutMiddle>
    </Container>
  );
};

const Container = styled(FullScreen)`
  text-align: center;
`;

const Title = styled(BaseText)<{ brand_color: BrandColorType }>`
  color: ${vars.color.textStrong};
  > strong {
    color: ${({ brand_color }) => brand_color.Brand_400};
  }
`;

const Content = styled(BaseText)`
  margin-top: 8px;
  padding: 0 30px;
  line-height: 140%;
  color: ${vars.color.textSecondary};
`;

const ConfirmButton = styled(NormalButton)`
  display: inline-block;
  min-width: 74px;
  margin-top: 24px;
  height: 42px;
  border-radius: 21px;
  padding: 0 14px;

  &:not(:last-child) {
    margin-right: 8px;
  }
`;
